import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Checkbox } from '@mui/material';

const ratingBuckets = ['High', 'Medium', 'Low'];

const PriceMatrix = ({ results = [], onFilterChange, formatPrice }) => {
    // Fake delivery dates if missing
    const FAKE_DATES = ['05/05-11/05', '12/05-18/05', '19/05-25/05', '26/05-01/06', '02/06-08/06'];
    let fakeIndex = 0;
    const resultsWithDates = results.map((r, i) => {
        if (!r.deliveryDate) {
            return { ...r, deliveryDate: FAKE_DATES[fakeIndex++ % FAKE_DATES.length] };
        }
        return r;
    });

    // Extract unique delivery dates
    const deliveryDates = Array.from(new Set(resultsWithDates.map(r => r.deliveryDate)));

    // State for selected dates and ratings
    const [selectedDates, setSelectedDates] = useState(deliveryDates);
    const [selectedRatings, setSelectedRatings] = useState(ratingBuckets);

    // Notify parent on change
    useEffect(() => {
        if (onFilterChange) {
            onFilterChange({ selectedDates, selectedRatings });
        }
    }, [selectedDates, selectedRatings, onFilterChange]);

    // Use placeholder values for the table
    const placeholderPrices = [12.5, 15.4, 7.3, 20.3, 9.7, 17.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5];
    let placeholderIdx = 0;
    const matrix = {};
    ratingBuckets.forEach(bucket => { matrix[bucket] = {}; });
    deliveryDates.forEach(date => {
        ratingBuckets.forEach(bucket => {
            matrix[bucket][date] = placeholderPrices[placeholderIdx % placeholderPrices.length];
            placeholderIdx++;
        });
    });

    // Find best and worst prices in the table
    const allPrices = [];
    ratingBuckets.forEach(bucket => {
        deliveryDates.forEach(date => {
            allPrices.push(matrix[bucket][date]);
        });
    });
    const sorted = [...allPrices].sort((a, b) => a - b);
    const best = sorted[0];
    const worst3 = sorted.slice(-3);

    return (
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
                variant="subtitle1"
                align="center"
                sx={{
                    fontWeight: 600,
                    color: '#888',
                    letterSpacing: 1,
                    mb: 0.5,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
            >
                Flexible Delivery Dates
            </Typography>
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 4,
                    boxShadow: 3,
                    width: '100%',
                    overflowX: 'hidden',
                }}
            >
                <Table
                    size="small"
                    sx={{
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                        tableLayout: 'fixed',
                        width: '100%',
                    }}
                >
                    <TableHead>
                        {/* Date checkboxes row */}
                        <TableRow>
                            <TableCell sx={{ background: 'transparent', border: 'none', width: { xs: 32, sm: 56 } }} />
                            <TableCell sx={{ background: 'transparent', border: 'none', width: { xs: 56, sm: 84 } }} />
                            {deliveryDates.map((date) => (
                                <TableCell key={date} align="center" sx={{ background: 'transparent', border: 'none', pb: 0 }}>
                                    <Checkbox
                                        checked={selectedDates.includes(date)}
                                        onChange={() => {
                                            setSelectedDates((prev) =>
                                                prev.includes(date)
                                                    ? prev.filter((d) => d !== date)
                                                    : [...prev, date]
                                            );
                                        }}
                                        sx={{ p: 0.5 }}
                                        inputProps={{ 'aria-label': `Show ${date}` }}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                        {/* Date headers row */}
                        <TableRow>
                            <TableCell
                                rowSpan={2}
                                sx={{
                                    background: 'transparent',
                                    border: 'none',
                                    width: { xs: 32, sm: 56 },
                                    p: 0,
                                }}
                            >
                                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            writingMode: 'vertical-rl',
                                            transform: 'rotate(-180deg)',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            color: '#888',
                                            letterSpacing: 1,
                                            mb: 0,
                                            fontSize: { xs: '0.75rem', sm: '0.9rem' },
                                        }}
                                    >
                                        Ratings
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ background: '#f7f7f7', border: 'none', width: { xs: 56, sm: 84 } }} />
                            {deliveryDates.map((date, idx) => (
                                <TableCell
                                    key={date}
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        background: '#f7f7f7',
                                        borderBottom: '2px solid #e0e0e0',
                                        borderTopRightRadius: idx === deliveryDates.length - 1 ? 16 : 0,
                                        textAlign: 'center',
                                        fontSize: { xs: '0.85rem', sm: '1.05rem' },
                                        whiteSpace: 'normal',
                                        lineHeight: 1.2,
                                        px: { xs: 0.5, sm: 1 },
                                    }}
                                >
                                    {date}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ratingBuckets.map((bucket, rowIdx) => (
                            <TableRow key={bucket}>
                                {/* Rating checkbox cell */}
                                <TableCell sx={{ background: 'transparent', border: 'none', width: { xs: 32, sm: 56 }, textAlign: 'center', p: 0 }}>
                                    <Checkbox
                                        checked={selectedRatings.includes(bucket)}
                                        onChange={() => {
                                            setSelectedRatings((prev) =>
                                                prev.includes(bucket)
                                                    ? prev.filter((b) => b !== bucket)
                                                    : [...prev, bucket]
                                            );
                                        }}
                                        sx={{ p: 0.5 }}
                                        inputProps={{ 'aria-label': `Show ${bucket} ratings` }}
                                    />
                                </TableCell>
                                {/* Rating label cell */}
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        background: '#f7f7f7',
                                        borderLeft: '2px solid #e0e0e0',
                                        borderBottomLeftRadius: rowIdx === ratingBuckets.length - 1 ? 16 : 0,
                                        textAlign: 'left',
                                        fontSize: { xs: '0.78rem', sm: '1.05rem' },
                                        pl: { xs: 1, sm: 2 },
                                        pr: 0.75,
                                        minWidth: { xs: 0, sm: 84 },
                                        whiteSpace: 'normal',
                                    }}
                                >
                                    {bucket}
                                </TableCell>
                                {deliveryDates.map((date, colIdx) => {
                                    const price = matrix[bucket][date];
                                    const isBest = price === best;
                                    const isWorst = worst3.includes(price);
                                    return (
                                        <TableCell
                                            key={date}
                                            align="center"
                                            sx={{
                                                fontWeight: isBest ? 700 : 500,
                                                color: isBest ? '#388e3c' : isWorst ? '#d32f2f' : 'inherit',
                                                background: isBest ? 'rgba(56, 142, 60, 0.10)' : isWorst ? 'rgba(211, 47, 47, 0.08)' : 'white',
                                                border: '1.5px solid #e0e0e0',
                                                borderRight: colIdx === deliveryDates.length - 1 ? '2px solid #e0e0e0' : undefined,
                                                borderBottom: rowIdx === ratingBuckets.length - 1 ? '2px solid #e0e0e0' : undefined,
                                                borderTop: 0,
                                                borderLeft: 0,
                                                fontSize: { xs: '0.85rem', sm: '1.1rem' },
                                                whiteSpace: 'nowrap',
                                                borderBottomRightRadius: rowIdx === ratingBuckets.length - 1 && colIdx === deliveryDates.length - 1 ? 16 : 0,
                                            }}
                                        >
                                            {price ? (formatPrice ? formatPrice(price) : `$${price.toFixed(2)}`) : '-'}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default PriceMatrix; 